import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { environment } from '@env/environment';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import { expand, Observable, of, OperatorFunction, take, tap, throwError, timeout, timer } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';

interface JobStatusDto {
  status: 'PENDING' | 'READY' | 'FAILED';
  url?: string;
}

interface PollState {
  jobId: string;
  attempt: number;
  result?: JobStatusDto;
}

@Injectable({
  providedIn: 'root'
})
export class ImportService {
  private httpClient = inject(HttpClient);

  constructor() {
    dayjs.extend(timezone);
  }

  /**
   * Generate and download the import template.
   * @param organizationId the organization ID
   * @param entityType the entity type
   * @param locale the locale to use
   * @param importConfigurationKey key of configuration related to import
   * @param isExport whether the template is filled with data
   * @param entityId Optional. Entity to fetch the import configuration from.
   * @return The template presignedURL
   */
  public downloadImportTemplate(
    organizationId: string,
    entityType: EntityTypeEnum,
    locale: string,
    importConfigurationKey: string = null,
    isExport = false,
    entityId: string = null
  ): Observable<JobStatusDto> {
    let url = environment.backend.baseUrl + '/template/generate'
      + `?organizationId=${organizationId}`
      + `&locale=${locale}`
      + `&entityType=${entityType}`
      + `&isExport=${isExport}`
      + `&timeZoneId=${dayjs.tz.guess()}`;

    if (importConfigurationKey != null) url += `&importConfigurationKey=${importConfigurationKey}`;
    if (entityId != null) url += `&entityId=${entityId}`;

    const pollUntilReady: OperatorFunction<string, JobStatusDto> = source$ => source$.pipe(
      // Start the job immediately
      map(jobId => ({jobId: jobId, attempt: 0})),

      // Poll the backend until the job is ready or failed
      expand(({jobId, attempt}) => {
        return this.httpClient.get<JobStatusDto>(`${environment.backend.baseUrl}/template/status/${jobId}`, {observe: 'response'})
          .pipe(
            switchMap(response => {
              // If the job is ready, return the presigned URL
              if (response.status === 200) {
                return of(<PollState>{jobId: jobId, attempt: attempt, result: response.body});
              }
              // If the job is still pending, poll again with expanding back off
              if (response.status === 202) {
                const delayMs = Math.min(2000 * Math.pow(2, attempt), 10000);
                return timer(delayMs).pipe(
                  map(() => (<PollState>{jobId: jobId, attempt: attempt + 1}))
                );
              }
              // If the job failed, throw an error
              return throwError(() => new Error('Generation failed'));
            })
          );
      }),
      // Only keep the response if it's ready and contains a result
      filter(value => !!value.result),
      take(1),
      map(value => value.result)
    );

    return this.httpClient.get(url, {observe: 'response', responseType: 'text'})
      .pipe(
        map(response => response.body),
        pollUntilReady,
        timeout(900000), // Timeout after 900 seconds
        tap(result => {
          if (result.status === 'READY' && result.url) {
            window.location.href = result.url;
          }
        })
      );
  }
}
